import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SecurityIcon from '@mui/icons-material/Security';
import ComputerIcon from '@mui/icons-material/Computer';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupsIcon from '@mui/icons-material/Groups';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from '../../api/axiosInstance';
import { useAuth } from '../../context/AuthContext';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatHours(value) {
  const number = Number(value || 0);
  return `${number.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
}

function progressValue(used, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((Number(used || 0) / Number(total || 1)) * 100));
}

function MetricCard({ icon, label, value, helper, color = 'primary.main' }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ color, display: 'inline-flex' }}>{icon}</Box>
          <Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
            {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function UsageCard({ title, used, total, footer }) {
  const numericUsed = Number(used || 0);
  const numericTotal = Number(total || 0);
  const percent = progressValue(numericUsed, numericTotal);
  const overrun = numericUsed > numericTotal && numericTotal > 0;
  const overrunHours = Math.max(0, numericUsed - numericTotal);
  const overrunPercent = numericTotal > 0 ? Math.round((overrunHours / numericTotal) * 100) : 0;
  const remaining = Math.max(0, numericTotal - numericUsed);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        borderColor: overrun ? 'error.main' : 'divider',
        bgcolor: overrun ? 'rgba(211, 47, 47, 0.06)' : 'background.paper',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Chip
            size="small"
            color={overrun ? 'error' : 'default'}
            label={overrun ? `+${overrunPercent}%` : `${percent}%`}
          />
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 800, color: overrun ? 'error.main' : 'text.primary' }}>
          {overrun ? `${formatHours(overrunHours)} dépassé` : `${formatHours(remaining)} disponible`}
        </Typography>
        <Typography variant="body2" color={overrun ? 'error.main' : 'text.secondary'} sx={{ fontWeight: overrun ? 700 : 400 }}>
          {formatHours(numericUsed)} utilisé / {formatHours(numericTotal)} prévu
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={overrun ? 'error' : 'primary'}
          sx={{ height: 8, borderRadius: 99 }}
        />
        {footer && <Typography variant="caption" color={overrun ? 'error.main' : 'text.secondary'}>{footer}</Typography>}
      </Stack>
    </Paper>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, height: '100%' }}>
      <Stack spacing={1.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}


function StaffContact({ title, user, icon }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: user ? 'primary.main' : 'text.disabled', display: 'inline-flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{user?.name || 'Non affecté'}</Typography>
        {user?.email ? <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography> : null}
      </Box>
    </Stack>
  );
}

export default function ClientDashboardHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usagePeriod, setUsagePeriod] = useState('month');

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/client/dashboard');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger votre tableau de bord.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const monthLabel = useMemo(() => {
    if (!data?.period?.monthStart) return 'Mois en cours';
    return new Date(data.period.monthStart).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const usageMultiplier = usagePeriod === 'year' ? 12 : 1;
  const usageData = usagePeriod === 'year' ? data.time?.year || {} : data.time?.month || {};
  const usageLabel = usagePeriod === 'year' ? 'année en cours' : 'mois en cours';
  const contractTotal = (data.contracts?.monthlyIncluded?.total || 0) * usageMultiplier;
  const monthTotal = data.time?.month?.total || 0;
  const usageTotal = usageData.total || 0;
  const plannedAdmin = (data.contracts?.monthlyIncluded?.admin || 0) * usageMultiplier;
  const plannedEngineer = (data.contracts?.monthlyIncluded?.engineer || 0) * usageMultiplier;

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: 1 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{ color: 'success.main', fontWeight: 800 }}>Espace client</Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {data.organization?.name || 'Tableau de bord'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bonjour {user?.firstname || ''}, synthèse des services, demandes et temps consommé.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={monthLabel} />
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadDashboard}>Actualiser</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard icon={<ConfirmationNumberIcon />} label="Tickets ouverts" value={data.tickets?.open || 0} helper={`${data.tickets?.total || 0} ticket(s) suivis`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard icon={<AccessTimeIcon />} label="Temps ce mois" value={formatHours(monthTotal)} helper={`${formatHours(data.contracts?.monthlyIncluded?.total || 0)} prévu au contrat`} color="warning.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard icon={<MicrosoftIcon />} label="Utilisateurs O365" value={data.services?.o365?.activeUsers || 0} helper={`${data.services?.o365?.used || 0}/${data.services?.o365?.total || 0} licences`} color="info.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard icon={<ComputerIcon />} label="Parc supervisé" value={data.services?.level?.devices || 0} helper={`${data.services?.eset?.devices || 0} appareil(s) ESET`} color="success.main" />
          </Grid>
        </Grid>

        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Consommation infogérance</Typography>
              <Typography variant="body2" color="text.secondary">Statistiques sur le {usageLabel}.</Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Période</InputLabel>
              <Select label="Période" value={usagePeriod} onChange={(event) => setUsagePeriod(event.target.value)}>
                <MenuItem value="month">Mois en cours</MenuItem>
                <MenuItem value="year">Année en cours</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <UsageCard title="Heures admin" used={usageData.admin} total={plannedAdmin} footer={usagePeriod === 'year' ? 'Prévu annuel estimé sur 12 mois' : `Année : ${formatHours(data.time?.year?.admin)}`} />
            </Grid>
            <Grid item xs={12} md={4}>
              <UsageCard title="Heures ingé" used={usageData.engineer} total={plannedEngineer} footer={usagePeriod === 'year' ? 'Prévu annuel estimé sur 12 mois' : `Année : ${formatHours(data.time?.year?.engineer)}`} />
            </Grid>
            <Grid item xs={12} md={4}>
              <UsageCard title="Total infogérance" used={usageTotal} total={contractTotal} footer={usagePeriod === 'year' ? 'Prévu annuel estimé sur 12 mois' : `Année : ${formatHours(data.time?.year?.total)}`} />
            </Grid>
          </Grid>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <SectionCard title="Vos référents">
              <Stack spacing={1.75}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: 'success.main', display: 'inline-flex' }}><AlternateEmailIcon /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">Support général</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{data.support?.email || 'support@toolbox.re'}</Typography>
                  </Box>
                </Stack>
                <Divider />
                <StaffContact title="Compte principal" user={data.portfolio?.primary} icon={<SupportAgentIcon />} />
                <StaffContact title="Binôme" user={data.portfolio?.binome} icon={<GroupsIcon />} />
                {(data.portfolio?.items || []).length ? (
                  <Stack spacing={1} sx={{ pt: 0.5 }}>
                    <Divider />
                    <Typography variant="caption" color="text.secondary">Référents par site</Typography>
                    {data.portfolio.items.slice(0, 3).map((item) => (
                      <Box key={item._id || item.siteName || item.description} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{item.siteName || item.description || 'Site'}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Principal : {item.primary?.name || '-'} · Binôme : {item.binome?.name || '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionCard title="Demandes récentes">
              <Stack divider={<Divider flexItem />}>
                {(data.tickets?.recent || []).length ? data.tickets.recent.map((ticket) => (
                  <Stack key={ticket._id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                    <Chip size="small" label={`#${ticket.ticketId}`} variant="outlined" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{ticket.subject || 'Sans sujet'}</Typography>
                      <Typography variant="caption" color="text.secondary">{ticket.assignedTo || 'Non assigné'} · {formatDate(ticket.updatedAt)}</Typography>
                    </Box>
                    <Chip size="small" label={ticket.status || 'Statut'} />
                  </Stack>
                )) : <Typography variant="body2" color="text.secondary">Aucune demande récente.</Typography>}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <SectionCard title="Services actifs">
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MicrosoftIcon color="info" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>Microsoft 365</Typography>
                    <Typography variant="caption" color="text.secondary">{data.services?.o365?.users || 0} compte(s), {data.services?.o365?.licenses || 0} licence(s)</Typography>
                  </Box>
                  <Chip size="small" label={`${data.services?.o365?.used || 0}/${data.services?.o365?.total || 0}`} />
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SecurityIcon color="success" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>ESET</Typography>
                    <Typography variant="caption" color="text.secondary">{data.services?.eset?.devices || 0} appareil(s) protégés</Typography>
                  </Box>
                  <Chip size="small" label={`${data.services?.eset?.used || 0}/${data.services?.eset?.total || 0}`} />
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Inventory2Icon color="warning" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>Level.io</Typography>
                    <Typography variant="caption" color="text.secondary">{data.services?.level?.devices || 0} device(s) référencés</Typography>
                  </Box>
                  <Chip size="small" label={`${data.services?.level?.online || 0} actifs`} />
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionCard title="Temps passés récents">
              <Stack divider={<Divider flexItem />}>
                {(data.time?.recent || []).length ? data.time.recent.map((entry) => (
                  <Stack key={entry._id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                    <Chip size="small" label={formatHours(entry.hours)} color="primary" variant="outlined" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{entry.ticketSubject || entry.activity || 'Intervention'}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(entry.spentOn)} · {entry.user || 'Technicien'}</Typography>
                    </Box>
                  </Stack>
                )) : <Typography variant="body2" color="text.secondary">Aucun temps passé récent.</Typography>}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Devices Level.io">
              <Stack divider={<Divider flexItem />}>
                {(data.services?.level?.recent || []).length ? data.services.level.recent.map((device) => (
                  <Stack key={device._id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                    <ComputerIcon fontSize="small" color="action" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{device.name || device.hostname || 'Device'}</Typography>
                      <Typography variant="caption" color="text.secondary">{device.model || device.osName || '-'} · {formatDate(device.lastSeenAt)}</Typography>
                    </Box>
                    <Chip size="small" label={device.status || 'Status'} variant="outlined" />
                  </Stack>
                )) : <Typography variant="body2" color="text.secondary">Aucun device Level.io rattaché.</Typography>}
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
